import { KeyboardArrowDown } from "@mui/icons-material";
import {
    FormControl,
    FormLabel,
    MenuItem,
    styled,
    TextField,
} from "@mui/material";
import { ChangeEventHandler, MouseEvent, useState } from "react";
import { createSelector } from "reselect";
import { IRootState } from "../../../redux/root.reducer";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { canAccessOtherUserListBuilder } from "../../../service/user-permission";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { listBuilderTableSetCurrentUserAction } from "../redux/list-builder-table-page.action";

const userSelector = createSelector(
    (state: IRootState) => state.listBuilderTable.listBuilderUsers,
    (state: IRootState) => state.listBuilderTable.listBuilderTable.currentUser,
    (childrenUsers, currentUser) => ({
        childrenUsers: childrenUsers.data
            ?.map((user) => ({
                email: user.email,
                name: user.username ?? user.email,
            }))
            .sort((a, b) => {
                const nameA = a.name.toLowerCase();
                const nameB = b.name.toLowerCase();
                return nameA.localeCompare(nameB);
            }),
        currentUser,
    }),
);

const UserDropdown = ({ className }: IWithClassName) => {
    const dispatch = useAppDispatch();

    const { childrenUsers, currentUser } = useAppSelector(userSelector);
    const userRole = useAppSelector(({ auth }: IRootState) => auth.user?.role);
    const isAuthorized = canAccessOtherUserListBuilder(userRole);

    const NOT_SELECTED = "not-selected";

    const [topPos, setTopPos] = useState<number>(0);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        const { top } = event.currentTarget.getBoundingClientRect();
        setTopPos(top + event.currentTarget.offsetHeight);
    };

    const handleUserChange: ChangeEventHandler<
        HTMLInputElement | HTMLTextAreaElement
    > = (event) => {
        const selectedUserEmail = event.target.value;
        dispatch(listBuilderTableSetCurrentUserAction(selectedUserEmail));
    };

    if (!isAuthorized) {
        return null;
    }
    const selectedUser = currentUser?.email ?? NOT_SELECTED;

    return (
        <FormControl variant="outlined" className={className}>
            <FormLabel component="legend">
                View another user&apos;s list:
            </FormLabel>
            <TextField
                id="user-select-dropdown"
                select
                value={selectedUser}
                onClick={handleClick}
                onChange={handleUserChange}
                variant="standard"
                SelectProps={{
                    IconComponent: KeyboardArrowDown,
                    MenuProps: {
                        sx: {
                            ".MuiPaper-root": {
                                maxHeight: `calc(95% - ${topPos}px)`,
                            },
                        },
                    },
                }}
            >
                <MenuItem value={NOT_SELECTED} disabled>
                    Select User
                </MenuItem>
                {childrenUsers?.map((user) => (
                    <MenuItem key={user.email} value={user.email}>
                        {user.name}
                    </MenuItem>
                ))}
            </TextField>
        </FormControl>
    );
};

export default styled(UserDropdown)(({ theme }: IWithThemeProps) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    "& .MuiFormControl-root": {
        margin: theme?.spacing(1),
    },
    "& .MuiFormLabel-root": {
        marginRight: theme?.spacing(1),
        "&.Mui-focused": {
            color: theme?.palette.listsTable.selectTextColor,
        },
    },
}));
